import { Suspense, lazy, useEffect, useState, createContext } from "react";
import { Routes, Route, BrowserRouter, Navigate } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useDispatch, useStore } from "react-redux";
import dayjs from "dayjs";
import { addYears } from "date-fns";

import LoadingScreen from "components/LoadingScreen";
import useStorage from "hooks/storage";
import useRequestHandler from "services/RequestHandler";
import useCookie from "hooks/cookie";
import NotificationManager from "components/NotificationManager";
import { getPublicNotifications } from "services/publicAPI";
import useNotification from "hooks/notification";
import { setupAxiosInterceptors } from "services/clients";

import "./App.css";
import "dayjs/locale/ro";
import { setDeviceSelection } from "reducers/deviceSlice";
import { watchDeviceViewport } from "util/deviceViewport";
import ProtectedRoute from "casl/ProtectedRoute";
import {
  getAuthorizations,
  getExchangeRate,
  getUserBackup,
  loadUser,
  signin,
} from "reducers/userSlice";

export const InstalableContext = createContext();

const NoPage = lazy(() => import("./pages/Error404"));
const DownPage = lazy(() => import("./pages/Error500"));

const Layout = lazy(() => import("./pages/authorized/Layout"));
const HomePage = lazy(() => import("./pages/authorized/Dashboard/NewHome"));
const DisabledAccountPage = lazy(
  () => import("./pages/public/DisabledAccount"),
);
const AccountPage = lazy(() => import("./pages/authorized/Account"));
const AccountPreferences = lazy(() => import("./pages/authorized/Preferences"));
const InvoicesSendListPage = lazy(
  () => import("./pages/authorized/InvoicesSendList"),
);

const InvoicesReceiveListPage = lazy(
  () => import("./pages/authorized/InvoicesReceiveList"),
);

const SoldCasaPage = lazy(
  () => import("./pages/authorized/RegistruCasaJurnal/SoldPage"),
);

const ExternInvoicesPage = lazy(
  () => import("./pages/authorized/ExternInvoicesList"),
);

// const ETVA = lazy(() => import("./pages/authorized/eTVA/ETVA"));

const InvoiceSendPage = lazy(
  () => import("./pages/authorized/InvoiceSendBulk/InvoicesSend"),
);

const InvoicesCsvSendPage = lazy(
  () => import("./pages/authorized/InvoiceSendCsv/InvoicesCsvSend"),
);

const RegisterSuccess = lazy(
  () => import("./pages/public/Register/RegisterSuccess"),
);
const RegisterFail = lazy(() => import("./pages/public/Register/RegisterFail"));

const InvoicesNewSimplePage = lazy(
  () => import("./pages/authorized/CreateNewInvoice"),
);
const InvoiceDraftsPage = lazy(
  () => import("./pages/authorized/Drafts/InvoiceDrafts"),
);

const RecurrentInvoicePage = lazy(
  () => import("./pages/authorized/RecurentInvoice/RecurrentInvoices"),
);

const RecurrentAfterSendPage = lazy(
  () => import("./pages/authorized/RecurentInvoice/ResultAfterSendRecurent.js"),
);

const RecurrentInvoiceEditor = lazy(
  () => import("./pages/authorized/RecurentInvoice/RecurrentInvoiceEditor.js"),
);
const RecurrentProductsEditor = lazy(
  () => import("./pages/authorized/RecurentInvoice/RecurrentProductsEditor.js"),
);
const CreateNewRecurrentInvoice = lazy(
  () => import("./pages/authorized/RecurentInvoice/CreateRecurrentInvoice.js"),
);
const AccountPageCompanies = lazy(
  () => import("./pages/authorized/Company/AccountPage"),
);

const ClientNotificationsPage = lazy(
  () => import("./pages/authorized/Notification/ClientNotifications"),
);

const SubscribePage = lazy(
  () =>
    import("./pages/authorized/PaymentSectionUser/SubscriptionPlanSelection"),
);
const InvitedAdminNoPlanPage = lazy(
  () => import("./pages/authorized/PaymentSectionUser/InvitedAdminNoPlan"),
);
const PageAPI = lazy(() => import("./pages/authorized/PageAPI"));
const ResultAfterPayPage = lazy(
  () => import("./pages/authorized/PaymentSectionUser/ResultAfterPay"),
);
const ResultBackUpConnection = lazy(
  () => import("./pages/authorized/Backup/BackUpConnectionResult"),
);
const ResultPayOpPage = lazy(
  () => import("./pages/authorized/PaymentSectionUser/ResultPayOp"),
);

const VATRates = lazy(() => import("./pages/authorized/CoteTVA/VATRates"));
const Payments = lazy(() => import("./pages/authorized/Payments/PaymentsPage"));

const AuthLayout = lazy(() => import("./pages/public/AuthLayout"));
const LoginPage = lazy(() => import("./pages/public/Login"));
const RegisterPage = lazy(() => import("./pages/public/Register"));

const ForgotPasswordSendEmailPage = lazy(
  () => import("./pages/public/ForgotPasswordSendEmail"),
);

const ConfirmPage = lazy(() => import("./pages/public/Confirm"));

const Callback = lazy(() => import("./pages/public/Callback"));
const AuthorizationLink = lazy(
  () => import("./pages/public/AuthorizationPage"),
);

const RegisterCompanyPage = lazy(
  () => import("./pages/public/Register/RegisterCompany"),
);
const RegisterDetailsCompanyPage = lazy(
  () => import("./pages/public/Register/RegisterDetailsCompany"),
);

const GeneralDetailsSectionPage = lazy(
  () => import("./pages/authorized/GeneralDetailsSection"),
);
const SerialRegisterPage = lazy(
  () => import("./components/CompanyDefinitions/Serials/SerialRegister"),
);
const InternProductCodeHomePage = lazy(
  () =>
    import("./components/CompanyDefinitions/InternProductCode/InternCodeHome"),
);
const InvoicePersonalizationPage = lazy(
  () => import("./pages/authorized/InvoicePersonalization"),
);

const PartnerDefinitionPage = lazy(
  () => import("./components/CompanyDefinitions/Partner/PartnerDefinition"),
);
const SerialDefinitionPage = lazy(
  () => import("./components/CompanyDefinitions/Serials/SerialDefinition"),
);
const ProductDefinitionPage = lazy(
  () => import("./components/CompanyDefinitions/Products/ProductCompany"),
);
const PaymentConfiguration = lazy(
  () => import("./pages/authorized/PaymentConfiguration/PaymentConfiguration"),
);
const DemoPage = lazy(
  () => import("./pages/authorized/PaymentSectionUser/Demo"),
);
const NotificationUserRedirectPage = lazy(
  () =>
    import("./pages/authorized/UserInfoNotification/NotificationUserRedirect"),
);
const ForgotPasswordResetPage = lazy(
  () => import("./pages/public/ForgotResetPassword"),
);
const ContactUsPage = lazy(() => import("./pages/authorized/ContactUs"));
const ShopPage = lazy(() => import("./pages/authorized/Shop/ShopPage"));
const SignatureValidationPage = lazy(
  () => import("./pages/authorized/SignatureValidation"),
);
const DocumentsPage = lazy(
  () => import("./pages/authorized/Documents/DocumentsPage"),
);
const DocumentLinkUploadPage = lazy(() => import("./pages/public/DocumentLinkUploadPage"));

const ConvertXmlToPdfPage = lazy(
  () => import("./pages/authorized/XmlToPDF/ConvertXmlToPdf.js"),
);

const GoodByeRemoveAccountPage = lazy(
  () => import("./pages/authorized/Account/GoodByeRemoveAccount"),
);
const NewTransportHomePage = lazy(
  () => import("./pages/eTransport/NewTransportHome"),
);
const EtransportPage = lazy(() => import("./pages/eTransport/Etransport"));
const ViewETransportHomePage = lazy(
  () => import("./pages/eTransport/ViewEtransport"),
);
const DraftsEtransPage = lazy(
  () => import("./pages/eTransport/components/TransportDrafts"),
);

const EtransTransportatorsPage = lazy(
  () => import("./pages/eTransport/Transporters/Transporters"),
);
const MobileNewTransportPage = lazy(
  () => import("./pages/eTransport/MobileNewTransport"),
);
const MobileViewEtransportPage = lazy(
  () => import("./pages/eTransport/MobileViewEtransport"),
);
const PreviewValidationComponentPage = lazy(
  () => import("./pages/authorized/RecurentInvoice/PreviewValidationComponent"),
);

const InvoiceFileExportPage = lazy(
  () => import("./pages/authorized/Export/InvoiceFileExport"),
);
const AuditUserLogsPage = lazy(
  () => import("./pages/authorized/AllLogs/AuditLogs"),
);
const InvoiceMessages = lazy(
  () => import("./components/InvoiceTable/InvoiceMessages.js"),
);

const BankAccountPage = lazy(() => import("./pages/authorized/BankAccount"));
const BankCallbackPage = lazy(
  () => import("./pages/authorized/BankAccount/Callback"),
);

setupAxiosInterceptors();

const App = () => {
  const store = useStore();
  const storage = useStorage();
  const { notify: notification } = useNotification();
  const dispatch = useDispatch();

  const notify = (message, id, type) => {
    notification({
      type,
      title: "Notificare de interes public",
      message,
      id,
      delay: 120,
    });
  };

  const { getCookie, setCookie } = useCookie("_es_ip");
  const [defPrompt, setPrompt] = useState(null);
  const [instalable, setInstalable] = useState(false);

  const handleSet = (value) => {
    const expDate = addYears(new Date(), 1);
    setCookie(value, { expires: expDate });
  };

  useEffect(() => watchDeviceViewport((mobile) => dispatch(setDeviceSelection(mobile))), [dispatch]);

  const handleInstall = () => {
    if (!defPrompt) return;

    defPrompt.prompt();
    defPrompt.userChoice.then((res) => {
      setPrompt(null);
      handleSet(false);
      setInstalable(false);
    });
  };

  const handleRefuse = () => {
    handleSet(false);
  };

  useRequestHandler();

  useEffect(() => {
    dayjs.locale("ro");
  }, []);

  useEffect(() => {
    getPublicNotifications()
      .then((res) => {
        res.data.forEach((notification) => {
          // console.log("notification is", notification.mesaj);
          notify(notification.mesaj, `user-${notification.id}`, "info");
        });
      })
      .catch((err) => {
        console.error("Failed to retrieve user notifications");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // console.debug("Subscribed app");

    let unsubscribe = store.subscribe(() => {
      //TODO: replace storage with indexeddb
      storage.put("redux", JSON.stringify(store.getState()));
    });

    return () => {
      // console.debug("Unsubscribed app");
      unsubscribe();
    };
  }, [store, storage]);

  useEffect(() => {
    const canInstall = getCookie();
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setInstalable(false);
      setCookie(false);
      return;
    }

    if (canInstall === undefined) setInstalable(true);

    const ready = (e) => {
      e.preventDefault();
      setPrompt(e);
    };

    window.addEventListener("beforeinstallprompt", ready);

    return () => {
      window.removeEventListener("beforeinstallprompt", ready);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleMessage = async (event) => {
      if (event?.data?.type === "IMPERSONATE") {
        const user = event.data.user;
        store.dispatch(signin(user));

        try {
          await loadUser(user);
          await getAuthorizations(user);
          await getUserBackup();
          await getExchangeRate();
        } catch (err) {
          console.error("Failed to initilize: ", err.message);
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => window.removeEventListener("message", handleMessage);
  }, [store]);

  return (
    <InstalableContext.Provider
      value={{
        canInstall: instalable,
        onAccept: handleInstall,
        onRefuse: handleRefuse,
      }}
    >
      <NotificationManager />
      <HelmetProvider>
        <Helmet>
          <title>StillCo eFactura</title>
        </Helmet>

        <BrowserRouter
          future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
        >
          <Suspense fallback={<LoadingScreen />}>
            <Routes>
              <Route path="document-upload" element={<DocumentLinkUploadPage />} />
              {/* private routes, need auth */}
              <Route path="app" element={<Layout />}>
                <Route index element={<HomePage />} />
                <Route path="account" element={<AccountPage />} />
                <Route path="preferences" element={<AccountPreferences />} />
                <Route
                  path="notificationUser"
                  element={<NotificationUserRedirectPage />}
                />
                <Route path="invoices">
                  <Route path="out" element={<InvoicesSendListPage />} />
                  <Route path="in" element={<InvoicesReceiveListPage />} />
                  <Route
                    path="extern"
                    element={
                      <ProtectedRoute I="read" a="externe">
                        <ExternInvoicesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="registru" element={<SoldCasaPage />} />
                  <Route path="messages" element={<InvoiceMessages />} />
                  <Route
                    path="export"
                    element={
                      <ProtectedRoute I="read" a="exportDateContabile">
                        <InvoiceFileExportPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* <Route
                    path="etva"
                    element={
                      <ProtectedRoute I="read" a="eTVA">
                        <ETVA />
                      </ProtectedRoute>
                    }
                  /> */}
                  <Route path="editor" element={<InvoicesNewSimplePage />} />
                  <Route path="drafts" element={<InvoiceDraftsPage />} />
                  <Route
                    path="recurrent"
                    element={
                      <ProtectedRoute I="read" a="contracte">
                        <RecurrentInvoicePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="recaftersend"
                    element={<RecurrentAfterSendPage />}
                  />
                  <Route
                    path="recurrentEditor"
                    element={<RecurrentInvoiceEditor />}
                  />
                  <Route
                    path="recurrentProductsEditor"
                    element={<RecurrentProductsEditor />}
                  />
                  <Route
                    path="createRecurrent"
                    element={<CreateNewRecurrentInvoice />}
                  />
                  <Route
                    path="send"
                    element={
                      <ProtectedRoute I="read" a="trimitereXMLuriMultiple">
                        <InvoiceSendPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="sendCsv"
                    element={
                      <ProtectedRoute I="read" a="importCSV">
                        <InvoicesCsvSendPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="view" element={<ConvertXmlToPdfPage />} />
                  <Route
                    path="preview"
                    element={<PreviewValidationComponentPage />}
                  />
                  <Route index element={<InvoicesNewSimplePage />} />
                </Route>
                <Route path="defs">
                  {/* <Route path="partner" element={<PartnerDefinitionPage />} /> */}
                  <Route
                    path="partner"
                    element={<PartnerDefinitionPage external={false} />}
                  />
                  <Route
                    path="product"
                    element={<ProductDefinitionPage type={"eFactura"} />}
                  />
                  <Route path="payment" element={<PaymentConfiguration />} />
                  <Route path="vat" element={<VATRates />} />
                  <Route path="payments" element={<Payments />} />
                  <Route path="serial" element={<SerialDefinitionPage />} />
                  <Route
                    path="intProCod"
                    element={<InternProductCodeHomePage />}
                  />
                </Route>
                <Route path="logs">
                  <Route path="userLogs" element={<AuditUserLogsPage />} />
                </Route>
                <Route path="demo" element={<DemoPage />} />
                <Route
                  path="eTrans"
                // element={<ProtectedRoute I="read" a="eTransport" />}
                >
                  <Route
                    path="etransOptions"
                    element={
                      <ProtectedRoute I="read" a="eTransport">
                        <EtransportPage />{" "}
                      </ProtectedRoute>
                    }
                  >
                    <Route
                      path="eTransHome"
                      element={
                        <ProtectedRoute I="read" a="eTransport">
                          <NewTransportHomePage />{" "}
                        </ProtectedRoute>
                      }
                    />
                  </Route>
                  <Route
                    path="viewEtrans"
                    element={
                      <ProtectedRoute I="read" a="eTransport">
                        <ViewETransportHomePage />{" "}
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="draftsEtrans"
                    element={
                      <ProtectedRoute I="read" a="eTransport">
                        <DraftsEtransPage />{" "}
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="eTransHome"
                    element={
                      <ProtectedRoute I="read" a="eTransport">
                        <NewTransportHomePage />{" "}
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="mobile"
                    element={
                      <ProtectedRoute I="read" a="eTransport">
                        <MobileNewTransportPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="mobileViewEtrans"
                    element={
                      <ProtectedRoute I="read" a="eTransport">
                        <MobileViewEtransportPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="defs">
                    <Route
                      path="eTransPartner"
                      element={
                        <ProtectedRoute I="read" a="eTransport">
                          <PartnerDefinitionPage external={true} />{" "}
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="transportators"
                      element={
                        <ProtectedRoute I="read" a="eTransport">
                          <EtransTransportatorsPage />{" "}
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="eTransProduct"
                      element={
                        <ProtectedRoute I="read" a="eTransport">
                          <ProductDefinitionPage type={"eTransport"} />{" "}
                        </ProtectedRoute>
                      }
                    />
                  </Route>
                </Route>

                <Route path="config">
                  <Route
                    path="clientnotifications"
                    element={<ClientNotificationsPage />}
                  />
                </Route>
                <Route path="load" element={<LoadingScreen />} />
                <Route path="500" element={<DownPage />} />
                <Route path="*" element={<NoPage />} />
                <Route path="config">
                  <Route
                    path="generalDetails"
                    element={<GeneralDetailsSectionPage />}
                  />
                  <Route
                    path="serialRegister"
                    element={<SerialRegisterPage />}
                  />
                  <Route
                    path="InvPersonaliz"
                    element={<InvoicePersonalizationPage />}
                  />
                </Route>
                <Route path="companies">
                  <Route index element={<AccountPageCompanies />} />
                  <Route path="add" element={<AccountPageCompanies />} />
                </Route>
                
                <Route path="bank">
                  <Route element={<BankAccountPage />} index />
                  <Route path="callback" element={<BankCallbackPage />} />
                </Route>

                <Route
                  path="backuprescon"
                  element={<ResultBackUpConnection service="google" />}
                />
                <Route path="backup">
                  {/* <Route path="google" element={<ResultBackUpGDConnection />} /> */}
                  <Route
                    path="onedrive"
                    element={<ResultBackUpConnection service="onedrive" />}
                  />
                </Route>

                <Route path="subscribe">
                  <Route path="payResult" element={<ResultAfterPayPage />} />
                  <Route path="payResultOp" element={<ResultPayOpPage />} />
                  <Route index element={<SubscribePage />} />
                </Route>
                <Route
                  path="noAdminPlan"
                  element={<InvitedAdminNoPlanPage />}
                />
                <Route
                  path="API"
                  element={
                    <ProtectedRoute I="read" a="integrariAPI">
                      <PageAPI />
                    </ProtectedRoute>
                  }
                />
                <Route path="shop" element={<ShopPage />} />
                <Route path="signature-check" element={<SignatureValidationPage />} />
                <Route path="documents" element={<DocumentsPage />} />
                <Route path="contact" element={<ContactUsPage />} />
              </Route>
              {/* public routes, don't need auth */}
              <Route element={<AuthLayout />}>
                {/* <Route index element={<LoginPage />} /> */}
                <Route index element={<LoginPage />} />
                <Route path="500" element={<DownPage />} />
                <Route path="*" element={<NoPage />} />
                <Route path="regComp" element={<RegisterCompanyPage />} />
                <Route
                  path="regDetailComp"
                  element={<RegisterDetailsCompanyPage />}
                />

                <Route path="register" element={<RegisterPage />} />
                <Route path="registerSuccess" element={<RegisterSuccess />} />
                <Route path="disabled" element={<DisabledAccountPage />} />
                <Route path="registerFail" element={<RegisterFail />} />
                <Route path="confirm" element={<ConfirmPage />} />
                <Route path="callback" element={<Callback />} />
                <Route
                  path="authorize-company/:cid"
                  element={<AuthorizationLink />}
                />
                <Route
                  path="password"
                  element={<ForgotPasswordSendEmailPage />}
                />
                <Route path="reset" element={<ForgotPasswordResetPage />} />
                <Route path="goodbye" element={<GoodByeRemoveAccountPage />} />
                <Route
                  path="InvPersonaliz"
                  element={<InvoicePersonalizationPage />}
                />
              </Route>
              {/* <Route path="subscribe">
                  <Route path="payResult" element={<ResultAfterPayPage />} />
                  <Route path="payResultOp" element={<ResultPayOpPage />} />
                  <Route index element={<SubscribePage />} />
                </Route> */}
              <Route index element={<Navigate to="/auth" />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </HelmetProvider>
    </InstalableContext.Provider>
  );
};

export default App;
